const CHECK_INTERVAL = 60_000;
const REQUEST_TIMEOUT = 5_000;

export class ClientVersionMonitor {
  private active = false;
  private suspended = false;
  private detected = false;
  private timer: number | undefined;
  private request: AbortController | undefined;
  private pending: Promise<boolean> | undefined;
  private lastCheck = -Infinity;

  constructor(
    private readonly buildId: string,
    private readonly onUpdate: () => void,
  ) {}

  start(): void {
    if (this.active) return;
    this.active = true;
    document.addEventListener('visibilitychange', this.handleReturn);
    window.addEventListener('focus', this.handleReturn);
    window.addEventListener('online', this.handleReturn);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('vite:preloadError', this.handleImportError);
    this.resume();
  }

  dispose(): void {
    this.active = false;
    window.clearInterval(this.timer);
    this.request?.abort();
    document.removeEventListener('visibilitychange', this.handleReturn);
    window.removeEventListener('focus', this.handleReturn);
    window.removeEventListener('online', this.handleReturn);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('vite:preloadError', this.handleImportError);
  }

  check(force = false): Promise<boolean> {
    if (this.detected) return Promise.resolve(true);
    if (!this.active || this.suspended) return Promise.resolve(false);
    if (this.pending) return this.pending;
    if (!force && Date.now() - this.lastCheck < REQUEST_TIMEOUT) return Promise.resolve(false);
    this.lastCheck = Date.now();
    const request = new AbortController();
    this.request = request;
    const timeout = window.setTimeout(() => request.abort(), REQUEST_TIMEOUT);
    this.pending = this.readVersion(request).finally(() => {
      window.clearTimeout(timeout);
      this.request = undefined;
      this.pending = undefined;
    });
    return this.pending;
  }

  private async readVersion(request: AbortController): Promise<boolean> {
    try {
      const response = await fetch('/client-version.json', {
        cache: 'no-store',
        signal: request.signal,
      });
      if (!response.ok) return false;
      const version: unknown = await response.json();
      if (
        request.signal.aborted ||
        !this.active ||
        this.suspended ||
        typeof version !== 'object' ||
        version === null ||
        !('buildId' in version) ||
        typeof version.buildId !== 'string' ||
        !/^[a-f0-9]{64}$/u.test(version.buildId) ||
        version.buildId === this.buildId
      )
        return false;
      this.detected = true;
      window.clearInterval(this.timer);
      this.onUpdate();
      return true;
    } catch {
      // Connectivity and deployment gaps are not evidence of a client update.
      return false;
    }
  }

  private resume(): void {
    window.clearInterval(this.timer);
    if (!this.detected) this.timer = window.setInterval(this.handleReturn, CHECK_INTERVAL);
    this.handleReturn();
  }

  private readonly handleReturn = (): void => {
    if (document.visibilityState === 'visible') void this.check();
  };

  private readonly handleImportError = (): void => {
    // Do not suppress Vite's error: the existing error screen remains the fallback.
    void this.check(true);
  };

  private readonly handlePageHide = (): void => {
    this.suspended = true;
    window.clearInterval(this.timer);
    this.request?.abort();
  };

  private readonly handlePageShow = (): void => {
    this.suspended = false;
    this.lastCheck = -Infinity;
    this.resume();
    // A page restored immediately can still have the aborted request settling.
    if (this.request?.signal.aborted) void this.pending?.then(this.handleReturn);
  };
}
